import {
   ChangeDetectorRef,
   Component,
   Input,
   OnChanges,
   OnDestroy,
   SimpleChanges,
   ViewEncapsulation,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { MenuItem } from './menu-item';

@Component({
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-site-menu',
    styleUrls: ['./site-menu.component.scss'],
    templateUrl: './site-menu.component.html',
    standalone: false
})
export class SiteMenuComponent implements OnChanges, OnDestroy {
   @Input() expanded = false;

   @Input() menuItems: MenuItem[] = [];

   @Input() currentUser;

   /**
    * The active item in the sliding nav menu,
    * identified by its routerLink property,
    * which is used by the `trackBy` function below.
    */
   activeItem: Pick<MenuItem, 'routerLink'> | null;

   additionalMenuItems: MenuItem[] = [];

   collapsedMenuItems: MenuItem[] = [];

   afterAdditionalMenuItems: MenuItem[] = [];

   private destroyed$ = new Subject<void>();

   private menuItems$ = new BehaviorSubject<MenuItem[]>([]);
   private menusWithoutAreasSet = new Set<string>();

   constructor(router: Router, private changeDector: ChangeDetectorRef, private store: Store) {
      combineLatest([
         this.menuItems$.pipe(filter((items): items is MenuItem[] => !!items)),
         router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)),
      ])
         .pipe(takeUntil(this.destroyed$))
         .subscribe(([menuItems, e]) => {
            const normalizedUrl = e.urlAfterRedirects?.split('?')[0]?.slice(1) ?? '';
            const baseSegment = normalizedUrl.split('/')[0];
            const bestMatchingRouterLink = this.findBestMatchingRouterLink(menuItems, normalizedUrl);

            if (bestMatchingRouterLink) {
               this.activeItem = { routerLink: bestMatchingRouterLink };
            } else if (this.menusWithoutAreasSet.has(baseSegment)) {
               this.activeItem = { routerLink: baseSegment };
            } else {
               this.activeItem = { routerLink: normalizedUrl };
            }
         });
   }

   displayWith(item: MenuItem): string {
      return item.name;
   }

   getChildren(item: MenuItem): MenuItem[] | undefined {
      return item.children;
   }

   ngOnChanges(changes: SimpleChanges): void {
      // we use ngOnChanges here to avoid a race condition when both `expanded` and `menuItems`
      // are set in the same change detection pass.
      // eslint-disable-next-line @typescript-eslint/dot-notation

      if (changes['expanded'] || changes['menuItems']) {
         this.arrangeMenuItems();
         this.menuItems$.next(this.menuItems);

         const children = this.getChildrenFromParentsWithoutArea(this.menuItems);
         this.menusWithoutAreasSet = new Set(children);
      }
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   trackBy(id: number, item: MenuItem): string {
      if (item.breadCrumbText && item.name) {
         return `${item.routerLink} ${item.breadCrumbText} ${item.name}`;
      }
      if (item.routerLink?.startsWith('/')) {
         return item.routerLink;
      }
      return `/${item.routerLink}`;
   }

   /**
    * Arranges the full list of menu items into two lists:
    * 1. Menu items that should appear in the menu when collapsed.
    * 2. Menu items that should appear in the '...' sub-menu.
    */
   private arrangeMenuItems() {
      this.collapsedMenuItems = [];
      this.additionalMenuItems = [];
      this.afterAdditionalMenuItems = [];

      for (const item of this.menuItems) {
         if (!item.isHidden) {
            if (item.showInCollapsedMenu) {
               if (item.afterAdditionalMenuItems) {
                  this.afterAdditionalMenuItems.push(item);
               } else {
                  this.collapsedMenuItems.push(item);
               }
            } else if (!item.isSpacer) {
               this.additionalMenuItems.push(item);
            }
         }
      }
   }

   private getChildrenFromParentsWithoutArea(menuItems: MenuItem[]): string[] {
      const children: string[] = [];

      for (const item of menuItems) {
         if (!item.area && Array.isArray(item.children)) {
            for (const child of item.children) {
               if (typeof child.routerLink === 'string') {
                  children.push(child.routerLink);
               }
            }
         }
      }

      return children;
   }

   private findBestMatchingRouterLink(menuItems: MenuItem[], currentUrl: string): string | null {
      let bestMatchingRouterLink: string | null = null;
      let bestMatchingRouterLinkLength = -1;

      const visitMenuItems = (items: MenuItem[]): void => {
         for (const item of items) {
            if (typeof item.routerLink === 'string') {
               const normalizedRouterLink = this.normalizeRouterLink(item.routerLink);
               if (this.isMatchingRoute(currentUrl, normalizedRouterLink)) {
                  if (normalizedRouterLink.length > bestMatchingRouterLinkLength) {
                     bestMatchingRouterLink = item.routerLink;
                     bestMatchingRouterLinkLength = normalizedRouterLink.length;
                  }
               }
            }

            if (Array.isArray(item.children) && item.children.length > 0) {
               visitMenuItems(item.children);
            }
         }
      };

      visitMenuItems(menuItems);

      return bestMatchingRouterLink;
   }

   private normalizeRouterLink(routerLink: string): string {
      return routerLink.replace(/^\/+|\/+$/g, '');
   }

   private isMatchingRoute(currentUrl: string, normalizedRouterLink: string): boolean {
      return currentUrl === normalizedRouterLink || currentUrl.startsWith(`${normalizedRouterLink}/`);
   }
}
